import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AdminLayout from './pages/admin/AdminLayout';
import Landing      from './pages/Landing';
import Login        from './pages/Login';
import Signup       from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import Dashboard    from './pages/Dashboard';
import Library      from './pages/Library';
import Profile      from './pages/Profile';
import Credits        from './pages/Credits';
import PublicPricing  from './pages/PublicPricing';
import Transactions from './pages/Transactions';
import NewProject   from './pages/NewProject';
import ProjectDetail from './pages/ProjectDetail';
import ForgotPassword from './pages/ForgotPassword';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy  from './pages/PrivacyPolicy';
import PaymentReturn  from './pages/PaymentReturn';
import Explore        from './pages/Explore';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Routes>
          <Route path="/"              element={<RootRoute />} />
          <Route path="/login"         element={<Login />} />
          <Route path="/signup"          element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register"      element={<Navigate to="/signup" replace />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/payment-return" element={<PaymentReturn />} />
          <Route path="/home"          element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/library"       element={<PrivateRoute><Library /></PrivateRoute>} />
          <Route path="/profile"       element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/credits"       element={<PrivateRoute><Credits /></PrivateRoute>} />
          <Route path="/pricing"       element={<PublicPricing />} />
          <Route path="/transactions"  element={<PrivateRoute><Transactions /></PrivateRoute>} />
          <Route path="/new-project"   element={<PrivateRoute allowGuest><NewProject /></PrivateRoute>} />
          <Route path="/project/:id"   element={<PrivateRoute allowGuest><ProjectDetail /></PrivateRoute>} />
          <Route path="/terms"         element={<TermsOfService />} />
          <Route path="/privacy"       element={<PrivacyPolicy />} />
          <Route path="/admin/*"       element={<AdminRoute><AdminLayout /></AdminRoute>} />
          <Route path="/explore"       element={<PrivateRoute><Explore /></PrivateRoute>} />
          <Route path="*"              element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </AuthProvider>
  );
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading…</p>
      </div>
    );
  }
  // A guest holds a truthy `user` (real JWT-backed row) but must land on the
  // marketing page like any other unconverted visitor — not /home, which
  // PrivateRoute would immediately bounce back here, looping forever.
  return user && user.role !== 'guest' ? <Navigate to="/home" replace /> : <Landing />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role !== 'admin') return <Navigate to="/home" replace />;
  return <>{children}</>;
}

function PrivateRoute({ children, allowGuest = false }: { children: React.ReactNode; allowGuest?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  // Guests satisfy this route's user check — they hold a real, JWT-backed account —
  // but it's a throwaway (placeholder @coarchitect.internal email, no password) meant
  // for exactly one generate-preview flow. Every other authenticated screen (profile,
  // library, credits, ...) is off-limits until they sign up for real.
  if (user.role === 'guest' && !allowGuest) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default App;
